/**
 * RunnerV3UI — Real-time UI stream visualization
 *
 * Route: /runner-v3/:id/ui
 * Displays live frames from Runner v3 in UI mode
 * Polling frames every 1s via GET /api/v1/runner-v3/:id/ui/frames
 */

import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';
import { isLoggedIn } from '../stores/auth.store';
import { LoginPage } from './Login';
import { SkeletonBar, Button } from '../components/ui';
import { T } from '../styles/tokens';

interface RunnerMeta {
  id: string;
  name: string;
  os: string;
  arch: string;
  mode: string;
  status: 'online' | 'offline' | 'error';
  version: string;
  last_seen_at?: string;
}

interface UIFrameResponse {
  success: boolean;
  data?: {
    frame_id: string;
    size_bytes: number;
    uploaded_at: string;
  };
}

export function RunnerV3UI({ id }: { id: string }) {
  // Auth check
  if (!isLoggedIn.value) return <LoginPage />;

  // State
  const [runner, setRunner] = useState<RunnerMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [frameData, setFrameData] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [frameCount, setFrameCount] = useState(0);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [fps, setFps] = useState(0);
  const [frameBuffer, setFrameBuffer] = useState<number[]>([]);

  // Fetch runner metadata
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/runner-v3/${id}`) as any;
        if (res && res.success && res.data) {
          setRunner(res.data as RunnerMeta);
          // Auto-start streaming if mode is UI
          if (res.data.mode === 'ui') {
            setIsStreaming(true);
          }
        } else {
          setError('Runner não encontrado');
        }
      } catch (e: any) {
        setError(e?.message || 'Erro ao carregar runner');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Frame polling loop
  useEffect(() => {
    if (!isStreaming || !runner) return;

    const frameTimestamps: number[] = [];
    let intervalId: NodeJS.Timeout | null = null;

    const pollFrame = async () => {
      try {
        const fetchRes = await fetch(`/api/v1/runner-v3/${id}/frames`, { credentials: 'include' });
        if (!fetchRes.ok) return;
        const res = await fetchRes.blob();

        if (res && res.size > 0) {
          const url = URL.createObjectURL(res);
          setFrameData(url);

          // FPS calculation
          const now = Date.now();
          frameTimestamps.push(now);
          if (frameTimestamps.length > 30) frameTimestamps.shift();

          if (frameTimestamps.length >= 2) {
            const timeSpan = frameTimestamps[frameTimestamps.length - 1] - frameTimestamps[0];
            const calculatedFps = (frameTimestamps.length - 1) / (timeSpan / 1000);
            setFps(Math.round(calculatedFps * 10) / 10);
          }

          setFrameCount(c => c + 1);
          setLastUpdate(new Date());
        }
      } catch (e: any) {
        console.warn('Frame fetch failed:', e.message);
        // Continue polling even if one frame fails
      }
    };

    // Initial frame
    pollFrame();

    // Poll every 1s
    intervalId = setInterval(pollFrame, 1000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isStreaming, id]);

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <div style={{ padding: 12 }}><SkeletonBar width="60%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="40%" height={14} /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px', background: '#fee', color: '#c00', borderRadius: 8 }}>
        <h3>Erro</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (!runner) {
    return (
      <div style={{ padding: '24px' }}>
        <p>Runner não encontrado</p>
      </div>
    );
  }

  const isOnline = runner.status === 'online';
  const isUIMode = runner.mode === 'ui';

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px', color: T.text }}>
 {runner.name}
        </h1>
        <p style={{ color: T.textMuted, fontSize: '14px' }}>
          ID: {runner.id.substring(0, 12)}...
        </p>
      </div>

      {/* Status Panel */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
      }}>
        {/* Status Card */}
        <div style={{
          padding: '16px',
          background: T.bgCard,
          borderRadius: T.radiusMd,
          border: `2px solid ${isOnline ? T.success : T.textMuted}`,
        }}>
          <p style={{ fontSize: '12px', color: T.textMuted, textTransform: 'uppercase', marginBottom: '8px' }}>
            Status
          </p>
          <p style={{ fontSize: '20px', fontWeight: '700', color: isOnline ? T.success : T.textMuted }}>
 {isOnline ? 'Online' : 'Offline'}
          </p>
        </div>

        {/* Mode Card */}
        <div style={{
          padding: '16px',
          background: T.bgCard,
          borderRadius: T.radiusMd,
          border: `2px solid ${isUIMode ? '#3498db' : '#9b59b6'}`,
        }}>
          <p style={{ fontSize: '12px', color: T.textMuted, textTransform: 'uppercase', marginBottom: '8px' }}>
            Modo
          </p>
          <p style={{ fontSize: '20px', fontWeight: '700', color: isUIMode ? '#3498db' : '#9b59b6' }}>
 {isUIMode ? 'UI' : 'Headless'}
          </p>
        </div>

        {/* OS & Arch */}
        <div style={{
          padding: '16px',
          background: T.bgCard,
          borderRadius: T.radiusMd,
          border: `2px solid ${T.border}`,
        }}>
          <p style={{ fontSize: '12px', color: T.textMuted, textTransform: 'uppercase', marginBottom: '8px' }}>
            Plataforma
          </p>
          <p style={{ fontSize: '18px', fontWeight: '700', color: T.text }}>
            {runner.os} ({runner.arch})
          </p>
        </div>

        {/* Last Seen */}
        <div style={{
          padding: '16px',
          background: T.bgCard,
          borderRadius: T.radiusMd,
          border: `2px solid ${T.border}`,
        }}>
          <p style={{ fontSize: '12px', color: T.textMuted, textTransform: 'uppercase', marginBottom: '8px' }}>
            Visto por último
          </p>
          <p style={{ fontSize: '14px', color: T.text, fontFamily: 'monospace' }}>
            {runner.last_seen_at ? new Date(runner.last_seen_at).toLocaleString('pt-BR') : 'N/A'}
          </p>
        </div>
      </div>

      {/* Control Buttons */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <Button variant="ghost"
          onClick={() => setIsStreaming(!isStreaming)}
          style={{
            padding: '12px 24px',
            background: isStreaming ? T.danger : '#27ae60',
            color: T.text,
            border: 'none',
            borderRadius: T.radiusMd,
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
          }}
        >
 {isStreaming ? 'Parar Transmissão' : 'Iniciar Transmissão'}
        </Button>

        {frameCount > 0 && (
          <div style={{
            padding: '12px 24px',
            background: T.bgCard,
            borderRadius: T.radiusMd,
            border: `2px solid ${T.border}`,
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
          }}>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '12px', color: T.textMuted }}>Frames</p>
              <p style={{ fontSize: '18px', fontWeight: '700', color: T.primary }}>
                {frameCount}
              </p>
            </div>
            <div style={{ width: '1px', height: '40px', background: T.border }} />
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '12px', color: T.textMuted }}>FPS</p>
              <p style={{ fontSize: '18px', fontWeight: '700', color: T.warning }}>
                {fps.toFixed(1)}
              </p>
            </div>
            <div style={{ width: '1px', height: '40px', background: T.border }} />
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '12px', color: T.textMuted }}>Última atualização</p>
              <p style={{ fontSize: '14px', color: T.text, fontFamily: 'monospace' }}>
                {lastUpdate ? `${lastUpdate.toLocaleTimeString('pt-BR')}` : 'N/A'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Frame Viewer */}
      <div style={{
        background: T.bg,
        borderRadius: T.radiusMd,
        border: `2px solid ${T.border}`,
        overflow: 'hidden',
        aspectRatio: '16/9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        minHeight: '400px',
      }}>
        {!isStreaming || !isOnline ? (
          <div style={{
            textAlign: 'center',
            color: T.textSecondary,
            padding: '24px',
          }}>
            <p style={{ fontSize: '18px', marginBottom: '8px' }}>
 {!isOnline ? 'Runner offline' : 'Transmissão pausada'}
            </p>
            <p style={{ fontSize: '14px', color: T.textMuted }}>
              {!isOnline ? 'Aguardando runner conectar...' : 'Clique em "Iniciar Transmissão" para começar'}
            </p>
          </div>
        ) : frameData ? (
          <img
            src={frameData}
            alt="Screen frame"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              background: T.bg,
            }}
          />
        ) : (
          <div style={{
            textAlign: 'center',
            color: T.textSecondary,
          }}>
            <p>Aguardando primeiro frame...</p>
          </div>
        )}

        {/* Overlay: Waiting for frames */}
        {isStreaming && isOnline && !frameData && (
          <div style={{
            position: 'absolute',
            bottom: '16px',
            right: '16px',
            padding: '8px 12px',
            background: 'transparent', backdropFilter: 'blur(10px)',
            borderRadius: '4px',
            color: T.text,
            fontSize: '12px',
          }}>
 Conectando...
          </div>
        )}
      </div>

      {/* Info Footer */}
      <div style={{
        marginTop: '24px',
        padding: '16px',
        background: T.bgCard,
        borderRadius: T.radiusMd,
        border: `1px solid ${T.border}`,
        fontSize: '13px',
        color: T.textMuted,
        lineHeight: '1.6',
      }}>
        <p>
 <strong>Dica:</strong>O frame atualiza a cada 1 segundo. Verifique se o runner v3 em modo UI está rodando no cliente.
        </p>
        <p style={{ marginTop: '8px' }}>
 <strong>Métricas:</strong>FPS calculado em tempo real. Se for 0, verifique a conexão do runner.
        </p>
      </div>
    </div>
  );
}
